"use client";

import { useCallback, useEffect, useState } from "react";
import { Briefcase, MessageSquare, Shield, Users } from "lucide-react";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { listComments, type PrototypeComment } from "@/lib/prototypes/comments";
import { TOURS } from "@/components/tours/catalog";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { CommentsDrawer, FeedbackView, PROTOTYPE_KEY } from "./Comments";
import { CommitteeView } from "./CommitteeView";
import { DRAFT, EXAMPLE, initialActivity, remindTarget, initialGroups, type ActivityItem, type Group, type GroupStage } from "./data";
import { GroupView } from "./GroupView";
import { VIEWS, type ViewId } from "./screens";
import { StaffView } from "./StaffView";

const ICONS = { staff: Briefcase, committee: Shield, group: Users } as const;
const STAGES: { id: GroupStage; label: string }[] = [
    { id: "plan", label: "Planning next year" },
    { id: "before", label: "Before a meeting" },
    { id: "after", label: "Just after a meeting" },
    { id: "done", label: "Minutes confirmed" },
];

/** The example committee's record at each point in its cycle, so all views agree. */
function exampleAt(base: Group, stage: GroupStage): Group {
    const i = STAGES.findIndex((s) => s.id === stage);
    let g: Group = { ...base, nextYear: i >= 1 ? "Approved" : base.nextYear };
    if (i >= 2) g = { ...g, held: base.held + 1, due: base.due + 1, lastMeeting: "12 Nov", lastStatus: "Waiting for minutes", daysWaiting: 0, nextMeeting: "14 Jan" };
    if (i >= 3) g = {
        ...g, onTime: base.onTime + 1, lastStatus: "Report sent",
        report: {
            meeting: "12 Nov", received: "Today", read: false,
            updates: `December results timeline agreed; annual review of the Exam Deferral, Cancellation, Retake, and Refund Policy done (no changes). ${DRAFT.attended} of 8 attended.`,
            decisions: "None", risks: "None raised.",
            projects: [{ name: "Item bank review", light: "green" }, { name: "Level III item writers", light: "amber" }],
        },
    };
    return g;
}

const STAGE_ACTIVITY: Record<GroupStage, string> = {
    plan: "",
    before: "Curriculum & Testing Committee approved its 2027 calendar — invitations sent",
    after: "Curriculum & Testing Committee meeting recorded — 6 of 8 present; draft minutes ready for the Chair",
    done: "Curriculum & Testing Committee minutes confirmed in 20 hours — report sent to the Governance Committee",
};

/**
 * Regular Meetings — clickable prototype. Three stakeholders: staff (see
 * everything), the Governance Committee (runs the governance projects and
 * follows up with committees) and the committees themselves (a deliberately simple
 * view: what to do, when, why). Sample data in local state; only reviewer
 * comments are saved.
 */
export default function RegularMeetingsPrototype() {
    const { user } = useWorkspace();
    const [view, setView] = useState<ViewId>("group");
    const [showComments, setShowComments] = useState(false);
    const [drawer, setDrawer] = useState(false);

    const [stage, setStageRaw] = useState<GroupStage>("plan");
    const [asChair, setAsChair] = useState(true);
    const [reminded, setReminded] = useState<string[]>([]);
    const [readIds, setReadIds] = useState<string[]>([]);
    const [activity, setActivity] = useState<ActivityItem[]>(initialActivity);

    const groups = initialGroups.map((g) => {
        const cur = g.id === EXAMPLE.id ? exampleAt(g, stage) : g;
        return cur.report && readIds.includes(g.id) ? { ...cur, report: { ...cur.report, read: true } } : cur;
    });

    const log = (text: string) => setActivity((a) => [{ at: "Just now", text }, ...a]);
    const setStage = (next: GroupStage) => {
        const from = STAGES.findIndex((s) => s.id === stage);
        const to = STAGES.findIndex((s) => s.id === next);
        for (let i = from + 1; i <= to; i++) log(STAGE_ACTIVITY[STAGES[i].id]);
        if (to < from) setReadIds((r) => r.filter((x) => x !== EXAMPLE.id));
        setStageRaw(next);
    };

    const [comments, setComments] = useState<PrototypeComment[]>([]);
    const [commentErr, setCommentErr] = useState<string | null>(null);
    const reload = useCallback(() => {
        listComments(PROTOTYPE_KEY).then((rows) => { setComments(rows); setCommentErr(null); })
            .catch((e) => setCommentErr((e as Error).message));
    }, []);
    useEffect(() => { reload(); }, [reload, user?.id]);

    const tourId = `staff.governance.${view}`;
    const { replay } = useAutoTour(tourId, TOURS[tourId] ?? [], { enabled: !!TOURS[tourId] && !showComments });
    const openFor = (id: string) => comments.filter((c) => c.screen_key === id && !c.resolved_at).length;

    return (
        <div className="space-y-4">
            {/* Who are you? */}
            <div id="gov-who" className="grid grid-cols-3 gap-2">
                {VIEWS.map((v) => {
                    const Icon = ICONS[v.id];
                    const active = view === v.id && !showComments;
                    return (
                        <button key={v.id} onClick={() => { setView(v.id); setShowComments(false); }}
                            className={`flex flex-col items-center gap-1 rounded-xl border p-2 text-center transition-colors sm:flex-row sm:items-start sm:gap-3 sm:p-3 sm:text-left ${active ? "border-[#003d45] bg-[#003d45] text-white" : "border-gray-200 bg-white hover:border-[#007a8a]"}`}>
                            <Icon className={`size-5 shrink-0 sm:mt-0.5 ${active ? "text-white" : "text-[#007a8a]"}`} />
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center justify-center gap-1 text-xs font-semibold leading-tight sm:justify-start sm:gap-2 sm:text-sm">
                                    {v.label}
                                    {openFor(v.id) > 0 && <span className="rounded-full bg-amber-400 px-1.5 text-[10px] text-[#003d45]">{openFor(v.id)}</span>}
                                </div>
                                <div className={`hidden text-xs sm:block ${active ? "text-white/70" : "text-gray-500"}`}>{v.blurb}</div>
                                <div className={`hidden text-[11px] sm:block ${active ? "text-white/50" : "text-gray-400"}`}>Viewing as {v.who}</div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
                {view === "group" && !showComments ? (
                    <div id="gov-demo-controls" className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-1.5 text-xs">
                        <span className="font-semibold text-amber-800">Prototype — show the committee:</span>
                        {STAGES.map((s) => (
                            <button key={s.id} onClick={() => setStage(s.id)}
                                className={`rounded-full px-2.5 py-0.5 font-medium ${stage === s.id ? "bg-[#003d45] text-white" : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"}`}>{s.label}</button>
                        ))}
                        <span className="mx-1 hidden text-amber-300 sm:inline">|</span>
                        {[["Chair", true], ["Member", false]].map(([label, chair]) => (
                            <button key={String(label)} onClick={() => setAsChair(chair as boolean)}
                                className={`rounded-full px-2.5 py-0.5 font-medium ${asChair === chair ? "bg-[#003d45] text-white" : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"}`}>
                                As {String(label).toLowerCase()}
                            </button>
                        ))}
                    </div>
                ) : <span />}
                <div className="flex flex-wrap items-center gap-3">
                    {!showComments && TOURS[tourId] && <TourButton onClick={replay} label="Explain this view" />}
                    <button onClick={() => setShowComments(!showComments)} className="text-xs font-semibold text-gray-500 hover:underline">
                        {showComments ? "Back to the prototype" : `All comments (${comments.length})`}
                    </button>
                    <button id="gov-comments-button" onClick={() => setDrawer(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-semibold text-[#003d45] hover:bg-amber-300">
                        <MessageSquare className="size-3.5" />Comment
                    </button>
                </div>
            </div>

            <div id="gov-body" className="rounded-2xl border border-gray-200 bg-gray-50/60 p-3 sm:p-6">
                {showComments ? <FeedbackView comments={comments} meId={user?.id} reload={reload} error={commentErr} />
                    : view === "staff" ? <StaffView groups={groups} activity={activity} reminded={reminded} />
                        : view === "committee" ? (
                            <CommitteeView groups={groups} reminded={reminded}
                                onRemind={(g) => { setReminded((r) => [...r, g.id]); log(`Governance Committee reminded ${remindTarget(g)} (${g.name})`); }}
                                onRead={(g) => setReadIds((r) => [...r, g.id])} />
                        ) : (
                            <GroupView stage={stage} asChair={asChair}
                                onApproveYear={() => setStage("before")}
                                onConfirm={() => setStage("done")} />
                        )}
            </div>

            <CommentsDrawer open={drawer} onClose={() => setDrawer(false)} tab={view} comments={comments} meId={user?.id} reload={reload} error={commentErr} />
        </div>
    );
}
